import { Card, CardContent } from "../client/components/ui/card";

export interface SessionAccountPnl {
  accountId: string | null;
  name: string | null;
  pnl: number;
}

interface SessionAccountsTableProps {
  accounts: any[];
  pnlByAccount: SessionAccountPnl[];
  t: any;
  /** If provided, rows are clickable (navigate to account detail). Mentor view omits this. */
  onAccountClick?: (accountId: string) => void;
}

/**
 * Barre d'objectif d'un challenge pendant une session : progression AVANT la
 * session (`pastRatio`), progression actuelle (`currentRatio`) et part de la
 * session (`sessionRatio`, signée). Tout est rapporté à l'objectif du challenge.
 */
export function sessionObjectiveRatios(account: any, sessionPnl: number) {
  const challenge = account.challengeProgress;
  const hasObjectiveBar = account.status === "challenge" && !!challenge && (challenge.targetPercent !== null || challenge.targetAmount !== null);
  let pastRatio = 0;
  let currentRatio = 0;
  let sessionRatio = 0;
  if (hasObjectiveBar) {
    const denom =
      challenge.targetAmount !== null
        ? challenge.targetAmount
        : (account.initialBalance * (challenge.targetPercent ?? 0)) / 100;
    if (denom > 0) {
      const profit = challenge.targetAmount !== null
        ? challenge.profit
        : (challenge.profitPercent ?? 0) * account.initialBalance / 100;
      currentRatio = Math.max(0, Math.min(1, profit / denom));
      pastRatio = Math.max(0, Math.min(1, (profit - sessionPnl) / denom));
      sessionRatio = sessionPnl / denom;
    }
  }
  return { hasObjectiveBar, pastRatio, currentRatio, sessionRatio };
}

export function SessionAccountsTable({ accounts, pnlByAccount, t, onAccountClick }: SessionAccountsTableProps) {
  if (!pnlByAccount.length) return null;
  const sessionPnlById = new Map<string, number>();
  for (const row of pnlByAccount) {
    if (row.accountId) sessionPnlById.set(row.accountId, row.pnl);
  }
  const rows = accounts.filter((a: any) => sessionPnlById.has(a.id));
  if (rows.length === 0) return null;

  const statusColors: Record<string, { dot: string; text: string; bg: string }> = {
    challenge: { dot: "bg-amber-400", text: "text-amber-500", bg: "bg-amber-400/10" },
    pa:        { dot: "bg-emerald-400", text: "text-emerald-500", bg: "bg-emerald-400/10" },
    live:      { dot: "bg-emerald-400", text: "text-emerald-500", bg: "bg-emerald-400/10" },
    demo:      { dot: "bg-blue-400", text: "text-blue-400", bg: "bg-blue-400/10" },
    inactive:  { dot: "bg-muted-foreground/40", text: "text-muted-foreground", bg: "bg-muted/40" },
  };
  const statusLabel: Record<string, string> = {
    challenge: t.tradingAccounts.statusChallenge,
    pa: "Funded",
    live: t.tradingAccounts.statusLive,
    demo: t.tradingAccounts.statusDemo,
    inactive: t.tradingAccounts.statusInactive,
  };

  return (
    <Card className="overflow-hidden">
      <div className="px-5 pt-4 pb-2 border-b border-border/50">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-primary/70">
          {t.sessionDetail.sessionAccountsTitle}
        </p>
      </div>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                <th className="px-3 py-2 text-left text-[9px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableName}</th>
                <th className="px-2 py-2 text-left text-[9px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableFirm}</th>
                <th className="px-2 py-2 text-left text-[9px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableStatus}</th>
                <th className="px-2 py-2 text-left text-[9px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableStrategy}</th>
                <th className="px-2 py-2 text-right text-[9px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableBalance}</th>
                <th className="px-2 py-2 text-right text-[9px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTablePnl}</th>
                <th className="px-3 py-2 text-left text-[10px] font-bold tracking-widest uppercase text-foreground min-w-[240px]">{t.dashboard.accountsTableObjective}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((account: any, idx: number) => {
                const balance = account.currentBalance ?? account.initialBalance;
                const pnl = balance - account.initialBalance;
                const pnlPct = account.initialBalance > 0 ? (pnl / account.initialBalance) * 100 : 0;
                const isPositive = pnl >= 0;
                const challenge = account.challengeProgress;
                const template = account.effectiveTemplate;
                const sc = statusColors[account.status] ?? statusColors.inactive;
                const isLast = idx === rows.length - 1;
                const sessionPnlForAccount = sessionPnlById.get(account.id) ?? 0;

                // Calcul partagé avec la version Lite (sessionObjectiveRatios).
                const { hasObjectiveBar, pastRatio, currentRatio, sessionRatio } = sessionObjectiveRatios(account, sessionPnlForAccount);

                const sessionPositive = sessionPnlForAccount >= 0;
                const clickable = !!onAccountClick;

                return (
                  <tr
                    key={account.id}
                    className={`group ${clickable ? "cursor-pointer hover:bg-muted/30 " : ""}transition-colors ${!isLast ? "border-b border-border/40" : ""}`}
                    onClick={clickable ? () => onAccountClick!(account.id) : undefined}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sc.dot}`} />
                        <span className="font-semibold text-xs truncate max-w-[120px]">{account.name}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        {template ? (
                          <span
                            className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full leading-none"
                            style={{ backgroundColor: template.color, color: template.textColor }}
                          >
                            {template.name}
                          </span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                        {account.activeTier && (
                          <span className="text-[9px] text-muted-foreground font-mono bg-muted px-1 py-0.5 rounded-sm leading-none">
                            {account.activeTier.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${sc.text} ${sc.bg}`}>
                        {statusLabel[account.status] ?? account.status}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      {account.strategies && account.strategies.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {account.strategies.map((s: any) => (
                            <span
                              key={s.id}
                              className="inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground"
                            >
                              <span
                                className="w-1 h-1 rounded-full shrink-0"
                                style={{ backgroundColor: s.color }}
                              />
                              {s.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <span className="font-mono font-semibold text-xs">
                        {balance.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {(() => {
                        const sessionPct = account.initialBalance > 0 ? (sessionPnlForAccount / account.initialBalance) * 100 : 0;
                        return (
                          <>
                            <div className={`font-mono font-semibold text-xs ${sessionPositive ? "text-[var(--edgely-pnl-positive)]" : "text-[var(--edgely-pnl-negative)]"}`}>
                              {sessionPositive ? "+" : ""}{sessionPnlForAccount.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </div>
                            <div className={`text-[10px] font-mono ${sessionPositive ? "text-[var(--edgely-pnl-positive)]" : "text-[var(--edgely-pnl-negative)]"} opacity-70`}>
                              {sessionPositive ? "+" : ""}{sessionPct.toFixed(2)}%
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    {/* Objective progress bar (emphasized) */}
                    <td className="px-3 py-2 min-w-[240px]">
                      {hasObjectiveBar ? (
                        <div>
                          <div className="flex justify-between text-xs mb-1.5 items-baseline">
                            <span className={`font-mono font-bold ${currentRatio >= 1 ? "text-emerald-500" : "text-foreground"}`}>
                              {challenge.targetAmount !== null
                                ? `${challenge.profit >= 0 ? "+" : ""}${challenge.profit.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} / ${challenge.targetAmount.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                                : `${Math.round(currentRatio * 100)}%`}
                            </span>
                            <span className="text-muted-foreground font-mono text-[11px]">{Math.round(currentRatio * 100)}%</span>
                          </div>
                          <div className="relative h-2 rounded-full bg-muted/60 overflow-hidden ring-1 ring-border/40">
                            {/* Past (pre-session) progress — muted base */}
                            <div
                              className={`absolute inset-y-0 left-0 ${currentRatio >= 1 ? "bg-emerald-500/40" : "bg-primary/35"}`}
                              style={{ width: `${Math.min(100, pastRatio * 100)}%` }}
                            />
                            {/* Session contribution — subtle accent segment */}
                            {sessionPositive
                              ? sessionRatio > 0 && (
                                  <div
                                    className="absolute inset-y-0"
                                    style={{
                                      left: `${Math.min(100, pastRatio * 100)}%`,
                                      width: `${Math.max(0, Math.min(100 - pastRatio * 100, sessionRatio * 100))}%`,
                                      background:
                                        "linear-gradient(180deg, rgba(52,211,153,0.95) 0%, rgba(16,185,129,0.85) 100%)",
                                    }}
                                    title={`${t.sessionDetail.sessionContribution}: +${sessionPnlForAccount.toFixed(2)}`}
                                  />
                                )
                              : sessionRatio < 0 && (
                                  <div
                                    className="absolute inset-y-0"
                                    style={{
                                      left: `${currentRatio * 100}%`,
                                      width: `${Math.max(0, (pastRatio - currentRatio) * 100)}%`,
                                      background:
                                        "linear-gradient(180deg, rgba(248,113,113,0.9) 0%, rgba(220,38,38,0.8) 100%)",
                                    }}
                                    title={`${t.sessionDetail.sessionContribution}: ${sessionPnlForAccount.toFixed(2)}`}
                                  />
                                )}
                            {/* Boundary tick: where the session begins on the bar */}
                            {((sessionPositive && sessionRatio > 0) || (!sessionPositive && sessionRatio < 0)) && (
                              <div
                                className="absolute top-0 bottom-0 w-px bg-foreground/50"
                                style={{
                                  left: `${(sessionPositive ? pastRatio : currentRatio) * 100}%`,
                                }}
                              />
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
